import { ErrorMessage, Field, type FieldProps } from 'formik';
import { object, string } from 'yup';

import { CustomErrorMessage } from '@design-system/error-message';
import { Textfield } from '@shared/fields/text-field';
import { TiptapEditor } from '@shared/components/reusables/tiptap-editor';

import {
  knowledgeRichTextsApi,
  type KnowledgeRichText,
} from '../services/knowledge-content-api';
import { ContentFormDialog } from './content-form-dialog';

type Values = { name: string; content: string };

const schema = object({
  name: string()
    .trim()
    .required('Text name is required')
    .max(255, 'At most 255 characters'),
  content: string().required('Enter some text'),
});

export function RichTextDialog({
  kbaseId,
  richText,
  open,
  onOpenChange,
}: {
  kbaseId: number;
  /** null adds a new text. */
  richText: KnowledgeRichText | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const create = knowledgeRichTextsApi.useCreate();
  const update = knowledgeRichTextsApi.useUpdate();

  return (
    <ContentFormDialog<Values>
      open={open}
      onOpenChange={onOpenChange}
      title={richText ? 'Edit Rich Text' : 'Add Rich Text'}
      subtitle='Train Your Model On Rich Texts'
      editing={richText !== null}
      isPending={create.isPending || update.isPending}
      initialValues={{
        name: richText?.name ?? '',
        content: richText?.content ?? '',
      }}
      validationSchema={schema}
      onSubmit={(values) =>
        richText
          ? update.mutateAsync({
              id: richText.id,
              name: values.name.trim(),
              content: values.content,
            })
          : create.mutateAsync({
              kbase: kbaseId,
              name: values.name.trim(),
              content: values.content,
            })
      }
    >
      <Textfield
        name='name'
        label='Text Name'
        placeholder='Enter text name'
      />
      <Field name='content'>
        {({ field, form }: FieldProps<string>) => (
          <div>
            <TiptapEditor
              value={field.value}
              onChange={(html) => form.setFieldValue(field.name, html)}
              placeholder='Enter your text here...'
              contentClassName='bg-primary-30 [&_.ProseMirror]:!min-h-[120px]'
            />
            <ErrorMessage
              name='content'
              component={CustomErrorMessage}
            />
          </div>
        )}
      </Field>
    </ContentFormDialog>
  );
}
